import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useLocation, useNavigate } from 'react-router';

const STORAGE_KEY = `persistedTableModels`;
// Per table, so old history entries keep working without the store growing forever
const MAX_ENTRIES = 20;

interface PersistedModels {
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  paginationModel: GridPaginationModel;
  currentTab?: string;
}

interface TableModels extends PersistedModels {
  setFilterModel(model: GridFilterModel): void;
  setSortModel(model: GridSortModel): void;
  setPaginationModel(model: GridPaginationModel): void;
  setCurrentTab?(tab: string): void;
}

type Store = Record<string, Record<string, PersistedModels>>;

function readStore(): Store {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || `{}`);
  }
  catch {
    return {};
  }
}

// Saves a table's filter/sort/pagination models to localStorage whenever they
// change, keyed by an id carried in the URL (?<table>Filters=<id>), so the
// browser back button (and a reload) lands on the same view. On mount, models
// stored for the id in the URL are re-applied; returns whether that happened
// so the table root can skip its first-mount reset.
export function usePersistedTableModels(table: string, models: TableModels): boolean {
  const param = `${table}Filters`;
  const location = useLocation();
  const navigate = useNavigate();
  const id = new URLSearchParams(location.search).get(param);
  const [stored] = useState(() => (id ? readStore()[table]?.[id] : undefined));
  const initial = useRef(models);
  const generatedId = useRef<string | undefined>(undefined);

  const { filterModel, sortModel, paginationModel, currentTab } = models;

  useEffect(() => {
    if (!stored) return;

    // Tab first: for actions it also rewrites the filter/sort models
    if (stored.currentTab && models.setCurrentTab) models.setCurrentTab(stored.currentTab);
    models.setFilterModel(stored.filterModel);
    models.setSortModel(stored.sortModel);
    models.setPaginationModel(stored.paginationModel);
  }, []);

  useEffect(() => {
    const first = initial.current;
    if (filterModel === first.filterModel
      && sortModel === first.sortModel
      && paginationModel === first.paginationModel
      && currentTab === first.currentTab) return;

    const key = id || generatedId.current || Date.now().toString(36);
    generatedId.current = key;

    const store = readStore();
    const entries = Object.entries(store[table] || {}).filter(([k]) => k !== key);
    entries.push([key, { filterModel, sortModel, paginationModel, currentTab }]);
    store[table] = Object.fromEntries(entries.slice(-MAX_ENTRIES));

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    }
    catch { /* quota / private mode: nothing to restore later, back button falls through to defaults */ }

    if (!id) {
      const search = new URLSearchParams(location.search);
      search.set(param, key);
      navigate(`${location.pathname}?${search}`, { replace: true, state: location.state });
    }
  }, [filterModel, sortModel, paginationModel, currentTab]);

  return !!stored;
}
